import React, { useState, useEffect, useMemo, useRef } from 'react';
import { doc, getDoc, updateDoc, increment, collection, query, where, getDocs, setDoc, addDoc, serverTimestamp, onSnapshot } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { httpsCallable } from 'firebase/functions';

import SEO from '../components/shared/SEO';
import Breadcrumbs from '../components/ui/Breadcrumbs';
import PhoneInput from '../components/ui/PhoneInput';
import LocationSearchInput from '../components/ui/LocationSearchInput';
import SmsComplianceCheckboxes from '../components/ui/SmsComplianceCheckboxes';
import Calendar from '../components/ui/Calendar';
import BookingSuccessModal from '../components/modals/BookingSuccessModal';
import LargePreviewModal from '../components/modals/LargePreviewModal';
import { getTaxRate, sanitizeData } from '../utils/helpers';
import { sendEmail } from '../utils/api';
import { useToast } from '../contexts/ToastContext';

export default function UpdatedListingDetail({ listing, user, db, navigateTo, isMapsScriptLoaded, isAuthReady, createNotification, isUserAuthenticated }) {
    const toast = useToast();
    const [liveListing, setLiveListing] = useState(listing || {});
    const [selectedOption, setSelectedOption] = useState(null);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [showLargeImage, setShowLargeImage] = useState(false);
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [quantity, setQuantity] = useState(1);
    const [availableQuantity, setAvailableQuantity] = useState(listing?.inventory || 1);
    const [guestName, setGuestName] = useState('');
    const [guestEmail, setGuestEmail] = useState('');
    const [guestPhone, setGuestPhone] = useState('');
    const [billingZip, setBillingZip] = useState(''); 
    const [deliveryMethod, setDeliveryMethod] = useState('pickup');
    const [deliveryAddress, setDeliveryAddress] = useState('');
    const [deliveryAddress2, setDeliveryAddress2] = useState('');
    const [deliveryTime, setDeliveryTime] = useState(''); // NEW: Delivery time state
    const [dynamicDeliveryFee, setDynamicDeliveryFee] = useState(0); // NEW: Dynamic pricing
    const [isCalculatingDistance, setIsCalculatingDistance] = useState(false); // NEW
    const [appliedCoupon, setAppliedCoupon] = useState(null);
    const [couponCode, setCouponCode] = useState('');
    const [couponError, setCouponError] = useState('');
    const [vendorData, setVendorData] = useState(null);
    const [isVendorLoading, setIsVendorLoading] = useState(true);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState({ message: '', showCreateAccount: false });
    const [damageWaiverSelected, setDamageWaiverSelected] = useState(false);
    const [optInInsurance, setOptInInsurance] = useState(true); // NEW: Platform Insurance State
    const [currentMonth, setCurrentMonth] = useState(new Date());
    const [agreementAccepted, setAgreementAccepted] = useState(false);

    // FIX: Robust included items normalizer. Handles arrays, strings, and objects safely.
    const displayIncludedItems = useMemo(() => {
        const items = liveListing?.includedItems || listing?.includedItems;
        if (Array.isArray(items)) return items.filter(Boolean);
        if (typeof items === 'string') return items.split(',').map(i => i.trim()).filter(Boolean);
        if (typeof items === 'object' && items !== null) return Object.values(items).filter(Boolean);
        return [];
    }, [liveListing?.includedItems, listing?.includedItems]);

    // FIX: Check if vendor is currently away (for banner display only)
    const isVendorCurrentlyAway = useMemo(() => {
        if (!vendorData?.vacationSettings?.enabled) return false;
        const { startDate, endDate } = vendorData.vacationSettings;
        if (!startDate || !endDate) return false;
        const today = new Date().toISOString().split('T')[0];
        return today >= startDate && today <= endDate;
    }, [vendorData]);

    // NEW: Check if the *selected dates* conflict with the vacation
    const isSelectedDateBlockedByVacation = useMemo(() => {
        if (!vendorData?.vacationSettings?.enabled || !startDate) return false;
        const { startDate: vStart, endDate: vEnd } = vendorData.vacationSettings;
        if (!vStart || !vEnd) return false;
        
        // Simple string comparison works for YYYY-MM-DD
        const start = startDate;
        const end = endDate || startDate;
        
        // Check for overlap: (StartA <= EndB) and (EndA >= StartB)
        return start <= vEnd && end >= vStart;
    }, [vendorData, startDate, endDate]);

    // FIX: Only 'venues' definitely don't need a location provided by the renter.
    // Services like DJs, Food, Entertainment, etc., need an event address, which we treat as "delivery" for data purposes.
    const nonReturnableCategories = ['venues']; 
    const isShippable = !nonReturnableCategories.includes(listing?.category);

    const imageUrls = useMemo(() => {
        const allUrls = new Set(liveListing?.imageUrls || []);
        (liveListing?.options || []).forEach(opt => {
            if (opt.imageUrl) allUrls.add(opt.imageUrl);
        });
        return allUrls.size > 0 ? Array.from(allUrls) : ['https://placehold.co/800x500/f2a2e8/ffffff?text=Large+View'];
    }, [liveListing]);

    useEffect(() => {
        if (!isAuthReady || !db || !listing?.id) return;

        const fetchVendor = async () => {
            setIsVendorLoading(true);
            if (listing.vendorId) {
                const vendorRef = doc(db, "users", listing.vendorId);
                const vendorSnap = await getDoc(vendorRef);
                if (vendorSnap.exists()) setVendorData(vendorSnap.data());
            }
            setIsVendorLoading(false);
        };
        fetchVendor();

        const listingRef = doc(db, "listings", listing.id);
        updateDoc(listingRef, { viewCount: increment(1) }).catch(err => console.error("Failed to increment view count:", err));

        const unsubListing = onSnapshot(listingRef, (docSnap) => {
            if (docSnap.exists()) {
                setLiveListing({ id: docSnap.id, ...docSnap.data() });
            }
        });

        return () => unsubListing();
    }, [listing?.id, listing?.vendorId, isAuthReady, db]);

    // Coupon Handler
    const handleApplyCoupon = () => {
        setCouponError('');
        if (!vendorData || !vendorData.coupons) {
            setCouponError('Invalid coupon code.');
            return;
        }
        const found = vendorData.coupons.find(c => c.code === couponCode.toUpperCase().trim());
        if (found) {
            setAppliedCoupon(found);
            setCouponCode('');
        } else {
            setCouponError('Invalid coupon code.');
            setAppliedCoupon(null);
        }
    };

    const removeCoupon = () => setAppliedCoupon(null);

    // NEW: Calculate Driving Distance for Per-Mile Delivery Pricing
    useEffect(() => {
        const calculateDynamicFee = async () => {
            if (liveListing.deliveryPricingType === 'per_mile' && deliveryMethod === 'delivery' && deliveryAddress && isMapsScriptLoaded && window.google) {
                setIsCalculatingDistance(true);
                try {
                    const service = new window.google.maps.DistanceMatrixService();
                    service.getDistanceMatrix({
                        origins: [liveListing.location || vendorData?.businessInfo?.address || 'Simi Valley, CA'],
                        destinations: [deliveryAddress],
                        travelMode: 'DRIVING',
                        unitSystem: window.google.maps.UnitSystem.IMPERIAL
                    }, (response, status) => {
                        if (status === 'OK' && response.rows[0].elements[0].status === 'OK') {
                            const miles = response.rows[0].elements[0].distance.value * 0.000621371; // meters to miles
                            setDynamicDeliveryFee(miles * (parseFloat(liveListing.deliveryFee) || 0));
                        }
                        setIsCalculatingDistance(false);
                    });
                } catch (err) {
                    console.error("Distance Matrix Error:", err);
                    setIsCalculatingDistance(false);
                }
            } else {
                setDynamicDeliveryFee(0);
            }
        };
        
        // Debounce calculation to avoid spamming API while typing
        const timeoutId = setTimeout(calculateDynamicFee, 800);
        return () => clearTimeout(timeoutId);
    }, [deliveryAddress, liveListing.deliveryPricingType, liveListing.deliveryFee, liveListing.location, vendorData?.businessInfo?.address, deliveryMethod, isMapsScriptLoaded]);

    useEffect(() => {
        const inventorySource = selectedOption || liveListing;
        if (!startDate) {
            setAvailableQuantity(inventorySource.inventory || 0);
            return;
        }

        let maxBookedOnDateRange = 0;
        const effectiveEndDate = endDate || startDate;
        let currentDate = new Date(startDate);
        while (currentDate <= new Date(effectiveEndDate)) {
            const dateString = currentDate.toISOString().split('T')[0];
            const dailyBookings = liveListing.bookedDates?.[dateString] || {};
            const bookedForOption = dailyBookings[selectedOption?.name || 'default'] || 0;
            maxBookedOnDateRange = Math.max(maxBookedOnDateRange, bookedForOption);
            currentDate.setDate(currentDate.getDate() + 1);
        }
        
        const available = Math.max(0, (inventorySource.inventory || 0) - maxBookedOnDateRange);
        setAvailableQuantity(available);
        if (quantity > available) {
            setQuantity(available > 0 ? available : 1);
        }

    }, [startDate, endDate, selectedOption, liveListing, quantity]);

    const { totalCost, renterFee, subtotal, discountAmount, calculatedDeliveryFee, calculatedTax, taxRate, calculatedInsuranceFee } = useMemo(() => {
        if (!startDate || quantity < 1) return { totalCost: '0.00', renterFee: '0.00', subtotal: '0.00', discountAmount: '0.00', calculatedDeliveryFee: '0.00', calculatedTax: '0.00', taxRate: 0, calculatedInsuranceFee: '0.00' };
        const start = new Date(startDate);
        const end = endDate ? new Date(endDate) : new Date(startDate);
        if (end < start) return { totalCost: '0.00', renterFee: '0.00', subtotal: '0.00', discountAmount: '0.00', calculatedDeliveryFee: '0.00', calculatedTax: '0.00', taxRate: 0, calculatedInsuranceFee: '0.00' };

        const diffTime = Math.abs(end - start);
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;

        let basePrice = diffDays * quantity * (selectedOption?.price || liveListing.price);
        if (damageWaiverSelected && liveListing.damageWaiver?.offered) {
            basePrice += liveListing.damageWaiver.fee;
        }

        // NEW: Calculate Platform Insurance Fee (Industry Standard Damage Waiver: 9%, Min $9)
        let insuranceCost = 0;
        if (optInInsurance) {
            insuranceCost = Math.max(9, basePrice * 0.09);
        }

        // Calculate Discount
        let discount = 0;
        if (appliedCoupon) {
            if (appliedCoupon.type === 'percent') {
                discount = basePrice * (appliedCoupon.amount / 100);
            } else {
                discount = appliedCoupon.amount;
            }
        }
        discount = Math.min(discount, basePrice); // Discount cannot exceed total
        
        // Calculate Delivery Fee
        let deliveryCost = 0;
        if (isShippable && deliveryMethod === 'delivery') {
             // Check for free delivery thresholds
             const isFreeQty = liveListing.freeDeliveryMinQuantity && quantity >= liveListing.freeDeliveryMinQuantity;
             const isFreeTotal = liveListing.freeDeliveryMinTotal && basePrice >= liveListing.freeDeliveryMinTotal;
             
             if (!isFreeQty && !isFreeTotal) {
                 deliveryCost = liveListing.deliveryPricingType === 'per_mile' ? dynamicDeliveryFee : (parseFloat(liveListing.deliveryFee) || 0);
             }
        }

        // Calculate Renter Fee FIRST (needed for tax base)
        const discountedBase = basePrice - discount;
        const RENTER_FEE_PERCENTAGE = 0.10;
        const calculatedRenterFee = discountedBase * RENTER_FEE_PERCENTAGE;

        // NEW: Calculate Tax (Industry Standard)
        // Tax is applied to the Total Transaction Value (Items + Delivery + Service Fee) in most marketplace jurisdictions.
        let effectiveZip = billingZip;
        if (isShippable && deliveryMethod === 'delivery' && deliveryAddress) {
            const zipMatch = deliveryAddress.match(/\b\d{5}\b/);
            if (zipMatch) {
                effectiveZip = zipMatch[0];
            }
        }
        const estimatedTaxRate = getTaxRate(effectiveZip);
        // FIX: Include insurance in taxable amount
        const taxableAmount = discountedBase + deliveryCost + calculatedRenterFee + insuranceCost;
        const taxAmount = taxableAmount * estimatedTaxRate;
        
        // FIX: Include delivery cost, service fee, and tax in total
        const calculatedTotalCost = taxableAmount + taxAmount;

        return {
            subtotal: basePrice.toFixed(2),
            discountAmount: discount.toFixed(2),
            renterFee: calculatedRenterFee.toFixed(2),
            calculatedDeliveryFee: deliveryCost.toFixed(2),
            calculatedInsuranceFee: insuranceCost.toFixed(2),
            calculatedTax: taxAmount.toFixed(2),
            taxRate: estimatedTaxRate,
            totalCost: calculatedTotalCost.toFixed(2),
        };
    }, [startDate, endDate, quantity, liveListing.price, selectedOption, damageWaiverSelected, liveListing.damageWaiver, appliedCoupon, deliveryMethod, isShippable, liveListing.deliveryFee, liveListing.deliveryPricingType, dynamicDeliveryFee, liveListing.freeDeliveryMinQuantity, liveListing.freeDeliveryMinTotal, billingZip, deliveryAddress, optInInsurance, liveListing.weekendMultiplier, liveListing.peakPeriods]);

    const handleNext = () => setCurrentIndex((prevIndex) => (prevIndex + 1) % imageUrls.length);
    const handlePrev = () => setCurrentIndex((prevIndex) => (prevIndex - 1 + imageUrls.length) % imageUrls.length);

    const handleShare = async () => {
        const shareData = {
            title: listing.title,
            text: `Check out this party rental: ${listing.title}`,
            url: window.location.href,
        };
        try {
            if (navigator.share) {
                await navigator.share(shareData);
            } else {
                // Fallback for browsers that don't support the Web Share API
                navigator.clipboard.writeText(window.location.href);
                // FIX: Replaced alert with toast
                toast.success("Link copied to clipboard!");
            }
        } catch (err) {
            console.error("Share failed:", err.message);
        }
    };

    const handleOptionSelect = (option) => {
        if (selectedOption?.name === option.name) {
            setSelectedOption(null);
            setCurrentIndex(0);
        } else {
            setSelectedOption(option);
            if (option.imageUrl) {
                const imageIndex = imageUrls.findIndex(url => url === option.imageUrl);
                if (imageIndex !== -1) {
                    setCurrentIndex(imageIndex);
                }
            }
        }
    };

    const handleRequestToBook = async () => {
        setError('');
        setSuccess({ message: '', showCreateAccount: false });

        // --- Validation ---
        if (!user) { navigateTo('login'); return; }
        if (user.uid === listing.vendorId) { setError("You cannot book your own items."); return; }
        if (isSelectedDateBlockedByVacation) { setError("The vendor is on vacation during these dates and cannot accept bookings."); return; } 
        if (user.isAnonymous && (!guestEmail || !guestName || !guestPhone)) { setError("Please fill out your name, email, and phone number to book as a guest."); return; }
        if (parseFloat(subtotal) <= 0) { setError("Please select valid dates and quantity."); return; }
        if (liveListing.options?.length > 0 && !selectedOption) { setError("Please select an option."); return; }
        if (quantity > availableQuantity) { setError(`Only ${availableQuantity} items are available for the selected dates.`); return; }
        
        let effectiveZip = billingZip;
        if (isShippable && deliveryMethod === 'delivery') {
            if (!deliveryAddress) { setError("Please enter a delivery address."); return; }
            if (!deliveryTime) { setError("Please select a requested delivery time."); return; } // NEW: Validate delivery time
            const zipMatch = deliveryAddress.match(/\b\d{5}\b/);
            if (zipMatch) {
                effectiveZip = zipMatch[0];
            } else {
                setError("Delivery address must include a valid zip code."); return;
            }
        } else {
            if (!billingZip || billingZip.length < 5) { setError("Please enter a valid billing zip code for tax calculation."); return; }
        }

        if (vendorData?.rentalAgreement && !agreementAccepted) { setError("You must accept the rental agreement to continue."); return; }
        
        try {
            // NEW: Real-time Inventory Conflict Detection
            const effectiveEndDate = endDate || startDate;
            const parseLocalDate = (dateStr) => {
                const [y, m, d] = dateStr.split('-');
                return new Date(y, m - 1, d);
            };
            
            const start = parseLocalDate(startDate);
            const end = parseLocalDate(effectiveEndDate);
            const buffer = parseInt(liveListing.bufferDays) || 0;
            end.setDate(end.getDate() + buffer);

            const bookingsQuery = query(
                collection(db, "bookings"),
                where("listingId", "==", listing.id),
                where("status", "in", ["pending", "awaitingPayment", "deposit-paid", "paid", "booked", "delivered", "awaitingPickup"])
            );
            
            const snapshot = await getDocs(bookingsQuery);
            const overlappingBookings = snapshot.docs.map(d => d.data()).filter(b => {
                if ((b.selectedOption || 'default') !== (selectedOption?.name || 'default')) return false;
                
                const bStart = parseLocalDate(b.date);
                const bEnd = parseLocalDate(b.returnDate || b.date);
                bEnd.setDate(bEnd.getDate() + (parseInt(liveListing.bufferDays) || 0));

                // Date overlap condition
                return start <= bEnd && end >= bStart;
            });

            let maxOverlapQty = 0;
            let loopDate = new Date(start);
            while (loopDate <= end) {
                let dailyQty = 0;
                overlappingBookings.forEach(b => {
                    const bStart = parseLocalDate(b.date);
                    const bEnd = parseLocalDate(b.returnDate || b.date);
                    bEnd.setDate(bEnd.getDate() + (parseInt(liveListing.bufferDays) || 0));

                    if (loopDate >= bStart && loopDate <= bEnd) {
                        dailyQty += parseInt(b.quantity) || 0;
                    }
                });
                maxOverlapQty = Math.max(maxOverlapQty, dailyQty);
                loopDate.setDate(loopDate.getDate() + 1);
            }

            const totalInv = selectedOption ? selectedOption.inventory : liveListing.inventory;
            if ((totalInv - maxOverlapQty) < quantity) {
                setError(`Inventory conflict: Only ${Math.max(0, totalInv - maxOverlapQty)} items are actually available. Another renter may have just booked this item.`);
                return;
            }

            const finalEndDate = endDate || startDate;
            const optionName = selectedOption?.name || 'default';
            
            // Generate a unique booking reference and number *before* saving
            const newBookingRef = doc(collection(db, "bookings"));
            const bookingNumber = `BK-${newBookingRef.id.slice(0, 8).toUpperCase()}`;
            
            const bookingData = {
                bookingNumber, 
                listingId: listing.id, listingTitle: listing.title,
                date: startDate, returnDate: finalEndDate,
                quantity: parseInt(quantity),
                selectedOption: optionName,
                renterId: user.uid,
                renterEmail: user.isAnonymous ? guestEmail : user.email,
                renterName: user.isAnonymous ? guestName : (user.plannerName || user.email),
                renterPhone: user.isAnonymous ? guestPhone : '',
                billingZip: effectiveZip, 
                isGuestBooking: user.isAnonymous === true, 
                vendorId: listing.vendorId,
                status: 'pending',
                createdAt: serverTimestamp(),
                subtotal: parseFloat(subtotal),
                discountAmount: parseFloat(discountAmount), 
                couponCode: appliedCoupon ? appliedCoupon.code : null, 
                totalCost: parseFloat(totalCost),
                renterFee: parseFloat(renterFee),
                insuranceFee: parseFloat(calculatedInsuranceFee), // NEW
                hasPlatformInsurance: optInInsurance, // NEW
                taxAmount: parseFloat(calculatedTax), 
                taxRate: parseFloat(taxRate), 
                deliveryMethod: isShippable ? deliveryMethod : 'na',
                deliveryFee: parseFloat(calculatedDeliveryFee), 
                deliveryAddress: isShippable && deliveryMethod === 'delivery' ? deliveryAddress : '',
                deliveryAddress2: isShippable && deliveryMethod === 'delivery' ? deliveryAddress2 : '',
                deliveryTime: isShippable && deliveryMethod === 'delivery' ? deliveryTime : '', // NEW: Save delivery time
            };
            
            // FIX: Sanitize booking data to prevent injection via custom fields
            const cleanBookingData = sanitizeData(bookingData);
            
            await setDoc(newBookingRef, cleanBookingData);

            // --- HOST-IT! DAMAGE GUARANTEE LOGIC ---
            if (optInInsurance) {
                console.log(`[Host-it! Guarantee] Applying damage guarantee for Booking ${bookingNumber}...`);
                // Internal record logic created for Host-it! Trust & Safety would go here.
            }

            await sendEmail(
                bookingData.renterEmail,
                `Booking Request Sent for ${listing.title} (#${bookingNumber})`,
                `
                    <div style="font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif; max-w: 600px; margin: 0 auto; background-color: #ffffff; border-radius: 12px; overflow: hidden; box-shadow: 0 4px 20px rgba(0,0,0,0.08); border: 1px solid #f3f4f6;">
                        <div style="background: linear-gradient(135deg, #ec4899 0%, #a855f7 100%); padding: 30px 20px; text-align: center;">
                            <h1 style="color: #ffffff; margin: 0; font-size: 24px; font-weight: bold;">Request Sent! ✉️</h1>
                        </div>
                        <div style="padding: 30px; color: #374151;">
                            <p style="font-size: 16px; margin-bottom: 20px;">Hi ${bookingData.renterName},</p>
                            <p style="font-size: 16px; margin-bottom: 20px; line-height: 1.5;">Your booking request for <strong>${listing.title}</strong> has been successfully sent to the vendor for approval.</p>
                            
                            <div style="background-color: #f9fafb; border-left: 4px solid #a855f7; padding: 15px; margin-bottom: 25px; border-radius: 4px;">
                                <p style="margin: 0 0 10px 0;"><strong>Order #:</strong> ${bookingNumber}</p>
                                <p style="margin: 0;"><strong>Dates:</strong> ${bookingData.date} to ${bookingData.returnDate}</p>
                            </div>
                            
                            <p style="font-size: 16px; margin-bottom: 20px; line-height: 1.5;">You will receive another email as soon as <strong>${user.vendorName || 'the vendor'}</strong> accepts or declines your request. Please save your Order # for your records, especially if you booked as a guest.</p>
                            
                            <div style="text-align: center; margin: 30px 0;">
                                <a href="${window.location.origin}/#bookingDetail/${newBookingRef.id}" style="background: #111827; color: #ffffff; padding: 12px 24px; text-decoration: none; border-radius: 6px; font-weight: bold; font-size: 16px; display: inline-block;">View Booking Details</a>
                            </div>
                            
                            <p style="font-size: 14px; color: #6b7280; margin-top: 30px; text-align: center;">Thanks for using Host-it!</p>
                        </div>
                    </div>
                `
            );
            // --- END EMAIL ---
            try {
                const destination = { page: 'bookingDetail', data: { booking: { id: newBookingRef.id } } };
                await createNotification(
                    listing.vendorId,
                    `New booking request (#${bookingNumber}) for ${listing.title} from ${user.isAnonymous ? guestName : (user.plannerName || user.email)}.`,
                    destination
                );
            } catch (notificationError) {
                console.warn("Could not create vendor notification, but booking succeeded. This is likely due to security rules.", notificationError);
            }

            // FIX: Toast instead of just setting state if you want immediate feedback, but keeping state for the UI message block is fine too.
            toast.success("Request sent successfully!");

            setSuccess({ 
                message: "Please save your booking number to easily find your booking later, especially for guest bookings.", 
                showCreateAccount: user.isAnonymous,
                bookingNumber: bookingNumber 
            });
            
            // Reset form state
            setStartDate(null);
            setEndDate(null);
            setQuantity(1);

        } catch (e) {
            console.error("Booking request failed: ", e);
            setError(e.message || "Could not send your booking request.");
        }
    };

    const startChat = async () => {
        // FIX: Ensure vendor data is loaded before attempting to create a chat document to prevent permissions errors.
        const vendorEmail = vendorData?.email || listing.vendorEmail;
        if (!vendorEmail) {
            // FIX: Replaced alert with toast
            toast.error("Cannot start chat. Vendor information is still loading.");
            return;
        }
        if (!user || user.isAnonymous) { navigateTo('login', listing); return; }
        const chatId = [user.uid, listing.vendorId].sort().join('_');
        const chatDocRef = doc(db, "chats", chatId);
        
        // FIX: The chat document is now created/updated with a `participantInfo` map that includes
        // names and profile picture URLs. This allows the chat UI to display avatars without
        // needing to make protected reads to user profiles, which would cause permission errors.
        await setDoc(chatDocRef, {
            participants: [user.uid, listing.vendorId],
            participantEmails: [user.email, vendorEmail],
            participantInfo: {
                [user.uid]: { name: user.plannerName || user.email, email: user.email, profilePicUrl: user.profilePicUrl || null },
                [listing.vendorId]: { name: vendorData.vendorName || vendorEmail, email: vendorEmail, profilePicUrl: vendorData.profilePicUrl || null }
            },
        }, { merge: true });

        // NEW: Add a starter template so the vendor knows which item is being discussed
        const prefillMessage = `Hi! I'm interested in your listing: "${listing.title}". Is it available?`;
        navigateTo('messages', { chatId, prefillMessage });
    };

    const isBookingDisabled = isVendorLoading ||
        (vendorData?.rentalAgreement && !agreementAccepted) ||
        (user && !user.isAnonymous && !isUserAuthenticated) ||
        isSelectedDateBlockedByVacation ||
        user?.uid === listing.vendorId; // FIX: Only disable booking if it's the user's own item

    const allOptions = [
        { name: 'Main', inventory: liveListing.inventory, price: liveListing.price, imageUrl: liveListing.imageUrls?.[0] },
        ...(liveListing.options || [])
    ];

    // FIX: Prevent application crash (blank page) if listing data is lost during the vendor auth redirect
    if (!listing) {
        return (
            <div className="min-h-[60vh] flex flex-col items-center justify-center p-10 bg-gray-50">
                <div className="bg-white p-8 rounded-xl shadow-sm border border-gray-200 text-center max-w-md">
                    <div className="w-16 h-16 bg-pink-100 text-pink-600 rounded-full flex items-center justify-center mx-auto mb-4 text-2xl">🔄</div>
                    <h2 className="text-xl font-bold text-gray-900 mb-2">Session Restored</h2>
                    <p className="text-gray-600 mb-6 text-sm">You have successfully signed in, but the listing you were viewing needs to be refreshed.</p>
                    <button onClick={() => navigateTo(user?.isVendor ? 'dashboard' : 'home')} className="bg-gray-900 text-white px-6 py-2.5 rounded-lg font-bold hover:bg-black transition shadow-sm w-full">
                        {user?.isVendor ? 'Go to Dashboard' : 'Return Home'}
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="container mx-auto relative">
            {/* FIX: Add SEO Component for specific listing */}
            <SEO 
                title={listing.title} 
                description={listing.description ? listing.description.substring(0, 160) : `Rent ${listing.title} for your next event.`} 
            />
            
            {/* NEW: Breadcrumbs for better navigation */}
            <Breadcrumbs 
                items={[
                    { label: 'Listings', action: () => navigateTo('listings') },
                    { label: listing.category || 'Item', action: () => navigateTo('listings', { category: listing.category }) },
                    { label: listing.title }
                ]} 
                navigateTo={navigateTo}
            />
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 pb-24 md:pb-0">
                <div className="lg:col-span-2 bg-white p-6 rounded-lg shadow-lg">
                    <div className="mb-6 relative group">
                        {/* FIX: Added onClick to open lightbox and cursor-zoom-in class */}
                        <img
                            src={imageUrls[currentIndex]}
                            onClick={() => setShowLargeImage(true)}
                            onError={(e) => { e.target.onerror = null; e.target.src='https://placehold.co/800x500/f2a2e8/ffffff?text=Image+Not+Found'; }}
                            alt={listing.title}
                            className="w-full h-96 object-cover rounded-lg mb-4 bg-gray-200 cursor-zoom-in hover:opacity-95 transition-opacity"
                        />
                        {imageUrls.length > 1 && (
                            <>
                                <button onClick={handlePrev} className="absolute top-1/2 left-2 -translate-y-1/2 bg-black bg-opacity-30 text-white p-2 rounded-full opacity-0 group-hover:opacity-100 transition-opacity">
                                    &lt;
                                </button>
                                <button onClick={handleNext} className="absolute top-1/2 right-2 -translate-y-1/2 bg-black bg-opacity-30 text-white p-2 rounded-full opacity-0 group-hover:opacity-100 transition-opacity">
                                    &gt;
                                </button>
                            </>
                        )}
                        <div className="flex space-x-2 overflow-x-auto p-1">
                            {imageUrls.map((url, index) => (
                                // FIX: Added onError handler to thumbnail images.
                                <img key={index} src={url} alt={`Thumbnail ${index + 1}`} onClick={() => setCurrentIndex(index)}
                                     onError={(e) => { e.target.onerror = null; e.target.src='https://placehold.co/80x80/e2e8f0/64748b?text=X'; }}
                                     className={`h-20 w-20 object-cover rounded-md cursor-pointer border-2 flex-shrink-0 bg-gray-200 ${currentIndex === index ? 'border-pink-500' : 'border-transparent'}`} />
                            ))}
                        </div>
                    </div>
                    <div className="flex justify-between items-start">
                        <div>
                            <div className="flex items-center gap-2 mb-2">
                                <button className={`text-sm ${liveListing.isService || listing.isService ? 'bg-purple-100 text-purple-700' : 'bg-pink-100 text-pink-700'} px-3 py-1 rounded-full font-semibold uppercase`}>
                                    {(liveListing.isService || listing.isService) && (liveListing.serviceType || listing.serviceType) ? (liveListing.serviceType || listing.serviceType) : (liveListing.category || listing.category)}
                                </button>
                                {/* NEW: Capacity Badge in Detail View */}
                                {liveListing.capacity && (
                                    <span className="text-sm bg-blue-100 text-blue-700 px-3 py-1 rounded-full font-semibold uppercase flex items-center gap-1">
                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                                            <path d="M13 6a3 3 0 11-6 0 3 3 0 016 0zM18 8a2 2 0 11-4 0 2 2 0 014 0zM14 15a4 4 0 00-8 0v3h8v-3zM6 8a2 2 0 11-4 0 2 2 0 014 0zM16 18v-3a5.972 5.972 0 00-.75-2.906A3.005 3.005 0 0119 15v3h-3zM4.75 12.094A5.973 5.973 0 004 15v3H1v-3a3 3 0 013.75-2.906z" />
                                        </svg>
                                        Capacity: {liveListing.capacity}
                                    </span>
                                )}
                            </div>
                            <h2 className="text-3xl font-bold">{liveListing.title || listing.title}</h2>
                            
                            {/* NEW: Detailed Star Rating */}
                            <div className="flex items-center mt-2 mb-4">
                                <div className="flex text-yellow-400 text-xl">
                                    {[...Array(5)].map((_, i) => (
                                        <span key={i}>{i < Math.round(liveListing.averageRating || 0) ? '★' : '☆'}</span>
                                    ))}
                                </div>
                                <span className="text-gray-700 font-bold text-lg ml-2">{(liveListing.averageRating || 0).toFixed(1)}</span>
                                <span className="text-gray-500 text-sm ml-1">({liveListing.reviewCount || 0} reviews)</span>
                            </div>
                        </div>
                        <button onClick={handleShare} className="p-2 rounded-full hover:bg-gray-100 text-gray-600">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8.684 13.342C8.886 12.938 9 12.482 9 12s-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.368a3 3 0 105.367 2.684 3 3 0 00-5.367-2.684z" />
                            </svg>
                        </button>
                    </div>
                    <p className="text-gray-500 mb-4">{liveListing.location || listing.location}</p>
                    
                    <div onClick={() => navigateTo('vendorProfile', { vendorId: listing.vendorId })} className="flex items-center mb-4 cursor-pointer group">
                        {/* FIX: Added onError handler to vendor profile picture. */}
                        <img
                            src={vendorData?.profilePicUrl || 'https://placehold.co/48x48/e2e8f0/64748b?text=V'}
                            onError={(e) => { e.target.onerror = null; e.target.src='https://placehold.co/48x48/e2e8f0/64748b?text=V'; }}
                            alt={vendorData?.vendorName}
                            className="w-12 h-12 rounded-full mr-3 object-cover bg-gray-200"
                        />
                        <div>
                            <p className="text-gray-500 text-sm">Sold by</p>
                            <p className="text-pink-600 font-semibold group-hover:underline transition-colors">{vendorData?.vendorName || listing.vendorEmail}</p>
                        </div>
                    </div>

                    {liveListing.dimensions && (
                        <div className="mb-4 flex items-center gap-2 text-gray-700 bg-gray-50 w-fit px-3 py-1.5 rounded-lg border border-gray-100">
                            <span className="text-lg">📏</span>
                            <span className="text-sm font-semibold">Dimensions:</span>
                            <span className="text-sm">{liveListing.dimensions}</span>
                        </div>
                    )}

                    {liveListing.idealFor && liveListing.idealFor.length > 0 && (
                        <div className="flex flex-wrap gap-2 mb-4">
                            {liveListing.idealFor.map(tag => (
                                <span key={tag} className="bg-green-100 text-green-800 text-xs font-semibold px-2.5 py-0.5 rounded-full">{tag}</span>
                            ))}
                        </div>
                    )}
                    <p className="text-gray-700 whitespace-pre-wrap">{liveListing.description || listing.description}</p>
                    
                    {displayIncludedItems.length > 0 && (
                        <div className="my-6 pt-6 border-t">
                            <div className="bg-green-50 p-4 rounded-lg">
                                <h4 className="font-bold text-lg text-green-800 mb-2">What's Included:</h4>
                                <ul className="list-disc list-inside text-green-700">
                                    {displayIncludedItems.map((item, index) => (
                                        <li key={index}>{typeof item === 'object' ? item.text || item.name || JSON.stringify(item) : item}</li>
                                    ))}
                                </ul>
                            </div>
                        </div>
                    )}
                    
                    {liveListing.category === 'venues' && (
                        <div className="my-6 pt-6 border-t">
                            <h3 className="text-2xl font-bold mb-4">Venue Details</h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div className="bg-blue-50 p-4 rounded-lg">
                                    <h4 className="font-semibold text-lg text-blue-800 mb-2">Amenities</h4>
                                    <ul className="list-disc list-inside text-blue-700">
                                        {liveListing.amenities?.map(item => <li key={item}>{item}</li>)}
                                    </ul>
                                </div>
                                <div className="bg-purple-50 p-4 rounded-lg">
                                    <h4 className="font-semibold text-lg text-purple-800 mb-2">House Rules</h4>
                                    <p className="text-purple-700 whitespace-pre-wrap">{liveListing.houseRules}</p>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
                <div className="lg:col-span-1">
                    {user && user.uid === listing.vendorId ? (
                        <div className="bg-white p-6 rounded-lg shadow-lg sticky top-28 text-center">
                            <h3 className="text-xl font-bold mb-2">This is Your Listing</h3>
                            <p className="text-gray-600 mb-4">Manage this item, view bookings, and edit details from your dashboard.</p>
                            <button onClick={() => navigateTo('dashboard')} className="w-full bg-pink-500 text-white py-3 rounded-lg font-semibold hover:bg-pink-600 transition">Go to Dashboard</button>
                        </div>
                    ) : (
                        // FIX: Added ID 'booking-card' for scrolling target
                        <div id="booking-card" className="bg-white p-6 rounded-lg shadow-lg sticky top-28">
                            <p className="text-2xl font-bold mb-4">
                                ${selectedOption?.price || liveListing.price} <span className="font-normal text-base">/ day</span>
                                {(liveListing.weekendMultiplier > 1 || (liveListing.peakPeriods && liveListing.peakPeriods.length > 0)) && (
                                    <span className="block text-xs text-pink-600 font-semibold mt-1">* Variable pricing applies for peak dates/weekends.</span>
                                )}
                            </p>
                            {liveListing.options && liveListing.options.length > 0 && (
                                <div className="mb-4">
                                    <h4 className="font-semibold mb-2">Options</h4>
                                    <div className="flex flex-wrap gap-2">
                                        {allOptions.map(opt => (
                                            <div key={opt.name} onClick={() => handleOptionSelect(opt)} className={`cursor-pointer border-2 rounded-lg p-1 text-center ${selectedOption?.name === opt.name ? 'border-pink-500' : 'border-transparent'}`}>
                                                {opt.imageUrl ? (
                                                    <img src={opt.imageUrl} alt={opt.name} className="w-12 h-12 rounded-md object-cover" />
                                                ) : (
                                                    <div className="px-3 py-1 bg-gray-200 rounded-full text-sm">{opt.name}</div>
                                                )}
                                                <p className="text-xs mt-1">{opt.name}</p>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                            <div className="space-y-4">
                               <Calendar 
                                    singleMonth={true} 
                                    bookedDates={liveListing.bookedDates || {}} 
                                    totalInventory={selectedOption ? selectedOption.inventory : (liveListing.inventory || 0)} 
                                    startDate={startDate} 
                                    setStartDate={setStartDate} 
                                    endDate={endDate} 
                                    setEndDate={setEndDate} 
                                    currentMonth={currentMonth} 
                                    setCurrentMonth={setCurrentMonth} 
                                    selectedOptionName={selectedOption?.name || 'default'}
                                    vacationSettings={vendorData?.vacationSettings}
                               />
                                <div>
                                    <label htmlFor="quantity" className="block text-sm font-medium text-gray-700">Quantity</label>
                                    <input type="number" id="quantity" value={quantity} onChange={e => setQuantity(Math.max(1, parseInt(e.target.value, 10)))} min="1" max={availableQuantity} className="mt-1 block w-full p-2 border rounded-md" />
                                    <p className="text-sm text-gray-600 mt-1">{availableQuantity} available for this option and date.</p>
                                </div>
                                {user && user.isAnonymous && (
                                    <div className="space-y-2">
                                        <input type="text" value={guestName} onChange={e => setGuestName(e.target.value)} placeholder="Full Name" className="w-full p-2 border rounded-md" required />
                                        <input type="email" value={guestEmail} onChange={e => setGuestEmail(e.target.value)} placeholder="Email Address" className="w-full p-2 border rounded-md" required />
                                        <PhoneInput value={guestPhone} onChange={e => setGuestPhone(e.target.value)} placeholder="Phone Number" className="w-full p-2 border rounded-md" required />
                                        <SmsComplianceCheckboxes />
                                    </div>
                                )}

                                {/* FIX: Restore Billing Zip Code Input */}
                                {(!isShippable || deliveryMethod !== 'delivery') && (
                                    <div className="mt-2">
                                        <label className="block text-sm font-medium text-gray-700">Zip Code <span className="text-gray-400 font-normal">(For Tax Calculation)</span></label>
                                        <input 
                                            type="text" 
                                            value={billingZip} 
                                            onChange={e => setBillingZip(e.target.value.replace(/[^0-9]/g, '').slice(0, 5))} 
                                            placeholder="e.g. 90210" 
                                            className="w-full p-2 border rounded-md" 
                                            maxLength="5"
                                        />
                                    </div>
                                )}

                                {/* NEW: Embedded Insurance API Toggle */}
                                <div className="mt-4 mb-4 p-4 bg-blue-50 border border-blue-100 rounded-xl flex gap-3 items-start cursor-pointer hover:bg-blue-100 transition-colors shadow-sm" onClick={() => setOptInInsurance(!optInInsurance)}>
                                    <input 
                                        type="checkbox" 
                                        checked={optInInsurance} 
                                        onChange={(e) => setOptInInsurance(e.target.checked)}
                                        onClick={(e) => e.stopPropagation()}
                                        className="mt-1 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded cursor-pointer shrink-0"
                                    />
                                    <div>
                                        <p className="text-sm font-bold text-blue-900 flex items-center gap-1.5">
                                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-blue-600" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M2.166 4.999A11.954 11.954 0 0010 1.944 11.954 11.954 0 0017.834 5c.11.65.166 1.32.166 2.001 0 5.225-3.34 9.67-8 11.317C5.34 16.67 2 12.225 2 7c0-.682.057-1.35.166-2.001zm11.541 3.708a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" /></svg>
                                            Add Host-it! Damage Guarantee
                                        </p>
                                        <p className="text-xs text-blue-700 mt-1 leading-relaxed">
                                            Protects you against accidental damage or stains during your rental period. (Excludes theft, loss, and intentional damage). Recommended for peace of mind.
                                        </p>
                                    </div>
                                    <span className="font-bold text-blue-900 text-sm whitespace-nowrap shrink-0">${optInInsurance ? calculatedInsuranceFee : Math.max(9, (parseFloat(subtotal) || 0) * 0.09).toFixed(2)}</span>
                                </div>

                                {/* NEW: Fulfillment Options (Delivery/Pickup) */}
                                {isShippable && (liveListing.canPickup !== false || liveListing.canDeliver) && (
                                    <div className="mt-4 border-t border-gray-100 pt-4">
                                        <label className="block text-sm font-bold text-gray-700 mb-3">Fulfillment Method</label>
                                        <div className="flex gap-4 mb-3">
                                            {liveListing.canPickup !== false && (
                                                <label className="flex items-center cursor-pointer">
                                                    <input 
                                                        type="radio" 
                                                        name="deliveryMethod" 
                                                        value="pickup" 
                                                        checked={deliveryMethod === 'pickup'} 
                                                        onChange={() => setDeliveryMethod('pickup')}
                                                        className="h-4 w-4 text-pink-600 focus:ring-pink-500 border-gray-300 mr-2 cursor-pointer"
                                                    />
                                                    <span className="text-sm font-medium text-gray-700">Pickup</span>
                                                </label>
                                            )}
                                            {liveListing.canDeliver && (
                                                <label className="flex items-center cursor-pointer">
                                                    <input 
                                                        type="radio" 
                                                        name="deliveryMethod" 
                                                        value="delivery" 
                                                        checked={deliveryMethod === 'delivery'} 
                                                        onChange={() => setDeliveryMethod('delivery')}
                                                        className="h-4 w-4 text-pink-600 focus:ring-pink-500 border-gray-300 mr-2 cursor-pointer"
                                                    />
                                                    <span className="text-sm font-medium text-gray-700">Delivery</span>
                                                </label>
                                            )}
                                        </div>

                                        {deliveryMethod === 'delivery' && (
                                            <div className="space-y-3 animate-fade-in bg-gray-50 p-4 rounded-xl border border-gray-100 mt-2 shadow-inner">
                                                <div>
                                                    <label className="block text-xs font-bold text-gray-500 uppercase tracking-wide mb-1">Delivery Address</label>
                                                    <LocationSearchInput 
                                                        initialValue={deliveryAddress} 
                                                        onPlaceSelected={setDeliveryAddress} 
                                                        isFormInput={true} 
                                                        isMapsScriptLoaded={isMapsScriptLoaded} 
                                                        placeholder="123 Party Lane, City, ST 90210"
                                                        autocompleteType="address"
                                                        className="w-full p-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-pink-500 outline-none bg-white"
                                                    />
                                                </div>
                                                <div>
                                                    <label className="block text-xs font-bold text-gray-500 uppercase tracking-wide mb-1">Apt, Suite, Unit (Optional)</label>
                                                    <input 
                                                        type="text" 
                                                        value={deliveryAddress2} 
                                                        onChange={e => setDeliveryAddress2(e.target.value)} 
                                                        className="w-full p-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-pink-500 outline-none bg-white" 
                                                        placeholder="Apt 4B"
                                                    />
                                                </div>
                                                <div>
                                                    <label className="block text-xs font-bold text-gray-500 uppercase tracking-wide mb-1">Requested Delivery Time</label>
                                                    <input 
                                                        type="time" 
                                                        value={deliveryTime} 
                                                        onChange={e => setDeliveryTime(e.target.value)} 
                                                        className="w-full p-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-pink-500 outline-none bg-white" 
                                                        required
                                                    />
                                                </div>
                                                {liveListing.deliveryFee > 0 && (
                                                    <p className="text-xs font-semibold text-gray-500 mt-1">
                                                        {isCalculatingDistance ? (
                                                            <span className="text-pink-500 animate-pulse">Calculating delivery distance...</span>
                                                        ) : liveListing.deliveryPricingType === 'per_mile' ? (
                                                            `* Delivery fee is $${parseFloat(liveListing.deliveryFee).toFixed(2)} per mile. Estimated fee: $${dynamicDeliveryFee.toFixed(2)}`
                                                        ) : (
                                                            `* Flat delivery fee of $${parseFloat(liveListing.deliveryFee).toFixed(2)} applies.`
                                                        )}
                                                        {liveListing.freeDeliveryMinTotal ? ` Free delivery on orders over $${liveListing.freeDeliveryMinTotal}.` : ''}
                                                    </p>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                )}

                                {/* FIX: Updated Vacation Notices */}
                                {isSelectedDateBlockedByVacation ? (
                                    <div className="mb-4 p-3 bg-red-50 text-red-800 rounded-md text-sm border border-red-200 flex items-start gap-2">
                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor">
                                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                                        </svg>
                                        <div>
                                            <strong>Unavailable</strong>
                                            <p className="mt-1">The vendor is on vacation during your selected dates.</p>
                                        </div>
                                    </div>
                                ) : isVendorCurrentlyAway && (
                                    <div className="mb-4 p-3 bg-yellow-50 text-yellow-800 rounded-md text-sm border border-yellow-200 flex items-start gap-2">
                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor">
                                            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                                        </svg>
                                        <div>
                                            <strong>Vendor Currently Away</strong>
                                            <p className="mt-1">This vendor is away until {new Date(vendorData.vacationSettings.endDate).toLocaleDateString()}. You can still book for future dates, but responses may be delayed.</p>
                                        </div>
                                    </div>
                                )}

                                <button onClick={handleRequestToBook} disabled={isBookingDisabled} className="w-full mt-4 bg-pink-500 text-white py-3 rounded-lg font-semibold hover:bg-pink-600 transition disabled:bg-gray-400 disabled:cursor-not-allowed">
                                    {isSelectedDateBlockedByVacation ? 'Dates Unavailable' : user?.uid === listing.vendorId ? 'Cannot Book Own Item' : 'Request to Book'}
                                </button>
                                <button onClick={startChat} className="w-full mt-2 bg-gray-700 text-white py-3 rounded-lg font-semibold hover:bg-gray-800 transition">Message Vendor</button>
                                {error && <p className="text-red-500 text-sm mt-2">{error}</p>}
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* NEW: Mobile Sticky Booking Footer */}
            <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-4 shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.1)] md:hidden z-[60] flex items-center justify-between animate-fade-in-up">
                <div className="flex flex-col">
                    <p className="text-gray-900 font-bold text-lg leading-tight">
                        ${selectedOption?.price || listing.price} 
                        <span className="text-sm font-normal text-gray-500"> / day</span>
                    </p>
                    {liveListing.averageRating > 0 && (
                        <div className="flex items-center gap-1 text-xs mt-0.5">
                            <span className="text-yellow-400">★</span>
                            <span className="font-medium text-gray-700">{liveListing.averageRating.toFixed(1)}</span>
                            <span className="text-gray-400">({liveListing.reviewCount})</span>
                        </div>
                    )}
                </div>
                <button 
                    onClick={() => {
                        const el = document.getElementById('booking-card');
                        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    }}
                    className="bg-pink-600 text-white px-6 py-3 rounded-lg font-bold shadow-md hover:bg-pink-700 transition"
                >
                    {user && user.uid === listing.vendorId ? 'Manage' : 'Check Dates'}
                </button>
            </div>

            {/* NEW: Lightbox Modal */}
            {showLargeImage && (
                <LargePreviewModal 
                    imageUrl={imageUrls[currentIndex]} 
                    onClose={() => setShowLargeImage(false)} 
                />
            )}
            
            {/* NEW: Booking Success Modal */}
            {success.message && (
                <BookingSuccessModal 
                    successData={success} 
                    onClose={() => setSuccess({ message: '', showCreateAccount: false })} 
                />
            )}
        </div>
    );
}